import DataLoader from 'dataloader'

import {
  CaseInput,
  CaseVersion,
  Mortgage,
  MortgageAndCase,
  MortgageInput,
  MortgageReason,
  MortgageStatus,
  PropertyInput,
  PropertyVersion,
  Scalars,
} from '../../generated/resolvers'
import {
  CdmGetMortgageRequest,
  CdmGetMortgageResponse,
  CdmGetPropertyRequest,
  CdmGetPropertyResponse,
} from '../../service/luther/model'
import { UUID } from '../../types'

const isSelectedOrProposed = ({ status }: Mortgage) => {
  return (
    status === MortgageStatus.StatusSelected ||
    status === MortgageStatus.StatusProposed ||
    status === MortgageStatus.StatusLenderProposed
  )
}

const isCurrent = ({ status }: Mortgage) => {
  return status === MortgageStatus.StatusCurrent
}

export const addPropertyOnCase = async ({
  input,
  caseId,
  propertyType,
  CaseLoader,
  addProperty,
  fetchMortgages,
  fetchMortgagesForCase,
  updateCase,
  updateMortgage,
  addMortgageToCase,
}: {
  input: PropertyInput
  caseId: Scalars['ID']
  propertyType: Scalars['String']
  CaseLoader: DataLoader<string, CaseVersion | null>
  addProperty: (input: PropertyInput) => Promise<PropertyVersion | null>
  getProperty: (params: CdmGetPropertyRequest) => Promise<CdmGetPropertyResponse>
  updateProperty: (input: PropertyInput, id: string) => Promise<PropertyVersion | null>
  fetchMortgages?: (params: CdmGetMortgageRequest) => Promise<Mortgage[] | null>
  fetchMortgagesForCase?: (case_id: UUID) => Promise<CdmGetMortgageResponse | null>
  updateCase: (input: CaseInput, id: string) => Promise<CaseVersion | null>
  updateMortgage: (mortgage_id: UUID, input: MortgageInput) => Promise<Mortgage | null>
  addMortgageToCase?: (mortgage_id: string, case_id: string) => Promise<MortgageAndCase>
}) => {
  const caseEntity = await CaseLoader.load(caseId)

  if (!caseEntity) {
    return null
  }

  const { change_of_preference_reason, preference_mortgage_reason } = caseEntity.details

  const propertyInput = input

  // when adding target property on btl, house move or ftb case registered_owners_details should no be set
  // on all other cases registered_owners_details should be included
  if (
    (preference_mortgage_reason === MortgageReason.ReasonBtl ||
      preference_mortgage_reason === MortgageReason.ReasonHouseMove ||
      preference_mortgage_reason === MortgageReason.ReasonFtb) &&
    propertyType === 'preference_target_property'
  ) {
    propertyInput.registered_owners_details = undefined
  }

  const newProperty = await addProperty(propertyInput)

  const caseInput = {
    [propertyType]: newProperty?.id,
    change_of_preference_reason: change_of_preference_reason ? [...change_of_preference_reason] : [],
  }

  //When adding a property to a case with a proposed or selected mortgage
  //ensure both property_Secured_ids and TargetProperty are set
  if (propertyType === 'preference_target_property' || propertyType === 'preference_related_property_sale') {
    // For target properties we want to update selected and proposed mortgages. But for
    // related sale properties we want update current mortgages instead
    let mortgages: Mortgage[] | undefined | null

    if (typeof fetchMortgagesForCase === 'function') {
      const response = await fetchMortgagesForCase(caseId)
      mortgages = response?.mortgages as Mortgage[]
    } else if (typeof fetchMortgages === 'function') {
      const response = await fetchMortgages({ filter_case_id: caseId })
      mortgages = response
    }

    if (mortgages?.length) {
      const filterFn = propertyType === 'preference_target_property' ? isSelectedOrProposed : isCurrent
      const mortgagesToUpdate = mortgages.filter(filterFn)

      mortgagesToUpdate.forEach(({ mortgage_id, property_secured_ids, mortgage_product_id }) => {
        const payload = { property_secured_ids: [...(property_secured_ids || []), newProperty?.id] }

        if (mortgage_id && mortgage_product_id) {
          updateMortgage(mortgage_id, payload as MortgageInput)
        } else if (!mortgage_id && !mortgage_product_id && addMortgageToCase) {
          addMortgageToCase(mortgage_id, caseId)
        }
      })
    }
  }

  return await updateCase(caseInput, caseId)
}
